/**
 * Thin wrappers around the staff API.
 *
 * Auth model: staff sign in directly on this back-office (passkey or
 * recovery phrase), so the `dr_staff_session` cookie is minted on this
 * origin. Every call sends `credentials: "include"` to forward it.
 * There is no password / pasted-token login — the back-office accepts
 * only WebAuthn passkeys and the staff recovery phrase.
 */

export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly code?: string) {
    super(message);
  }
}

async function handle<T>(resp: Response): Promise<T> {
  if (resp.status === 204) return undefined as unknown as T;
  const body = await resp.json().catch(() => null);
  if (!resp.ok) {
    const code = body?.error?.code as string | undefined;
    const message = body?.error?.message ?? resp.statusText;
    throw new ApiError(resp.status, message, code);
  }
  return body as T;
}

const MIRROR_API_ORIGINS: Record<string, string> = {
  "collectandtrack.lol": "https://api.collectandtrack.lol",
  "collectandtrack.xyz": "https://api.collectandtrack.xyz",
  "internal-cat.top": "https://api.internal-cat.top",
};

function detectApiBaseUrl(): string {
  const envBase = import.meta.env.VITE_API_BASE;
  if (envBase && envBase !== "/api") return envBase;
  if (typeof window !== "undefined") {
    const root = window.location.hostname.split(".").slice(-2).join(".");
    const mirrored = MIRROR_API_ORIGINS[root];
    if (mirrored) return mirrored;
  }
  return envBase ?? "/api";
}

const API_BASE = detectApiBaseUrl();

export async function api<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const resp = await fetch(`${API_BASE}${path}`, {

    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
  });
  return handle<T>(resp);
}

// ---------------- DTOs (kept loose; backend is the source of truth) -----

export interface ServiceKey {
  id: string;
  name: string;
  prefix: string;
  role: "admin" | "dev" | "service";
  permissions: string[];
  status: "pending" | "active" | "revoked" | "rejected";
  createdByStaffId: string;
  createdAt: string;
  validatedAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  revokedReason: string | null;
  rejectedReason: string | null;
  latestRunId: string | null;
}

export interface ValidationStep {
  ordering: number;
  command: string;
  status: "pending" | "running" | "ok" | "fail";
  output: string;
  durationMs: number | null;
}

export interface ValidationRun {
  id: string;
  keyId: string;
  startedAt: string;
  finishedAt: string | null;
  outcome: "success" | "failure" | null;
  failureReason: string | null;
  steps: ValidationStep[];
}

export interface InvoiceView {
  id: string;
  status: string;
  targetType: "offer" | "boost" | "subscription";
  targetId: string;
  fiatCurrency: string;
  amountFiatMinor: number;
  sourceCurrency: string;
  sourceAmount: string;
  receivingAddress: string;
  expiresAt: string;
  paidAt: string | null;
  createdAt: string;
}

// ---------------- Service keys -------------------------------------------

export const serviceKeys = {
  list: () => api<{ items: ServiceKey[] }>("/staff/service-keys"),
  get: (id: string) =>
    api<{ record: ServiceKey }>(`/staff/service-keys/${id}`),
  create: (input: {
    name: string;
    role: ServiceKey["role"];
    permissions: string[];
  }) =>
    api<{ plaintextKey: string; record: ServiceKey; validationRunId: string | null }>(
      "/staff/service-keys",
      { method: "POST", body: JSON.stringify(input) },
    ),
  revoke: (id: string, reason?: string) =>
    api<void>(`/staff/service-keys/${id}/revoke`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
  startValidation: (id: string, plaintextKey: string) =>
    api<{ runId: string }>(
      `/staff/service-keys/${id}/validation-runs`,
      { method: "POST", body: JSON.stringify({ plaintextKey }) },
    ),
  getRun: (id: string, runId: string) =>
    api<{ run: ValidationRun }>(
      `/staff/service-keys/${id}/validation-runs/${runId}`,
    ),
  /**
   * EventSource wrapper for the SSE stream. The browser doesn't allow
   * custom headers on EventSource — we rely on the cookie for auth
   * (Bearer fallback is unavailable here). The route already validates
   * the request server-side.
   */
  streamUrl: (id: string, runId: string): string =>
    `${API_BASE}/staff/service-keys/${id}/validation-runs/${runId}/stream`,
};

// Re-export InvoiceView (used by future payments admin pages).
export type { InvoiceView as _InvoiceViewExport };

// =====================================================================
// Staff passkeys (WebAuthn).
// =====================================================================
export interface StaffPasskeySummary {
  id: string;
  name: string;
  aaguid: string | null;
  transports: string | null;
  credentialDeviceType: string | null;
  backedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

export const passkeys = {
  registerOptions: (name: string) =>
    api<{ challengeId: string; options: unknown }>(
      "/staff/passkey/register/options",
      { method: "POST", body: JSON.stringify({ name }) },
    ),
  registerVerify: (challengeId: string, attestationResponse: unknown) =>
    api<{ passkey: StaffPasskeySummary }>(
      "/staff/passkey/register/verify",
      { method: "POST", body: JSON.stringify({ challengeId, attestationResponse }) },
    ),
  loginOptions: () =>
    api<{ challengeId: string; options: unknown }>(
      "/staff/passkey/login/options",
      { method: "POST", body: JSON.stringify({}) },
    ),
  loginVerify: (
    challengeId: string,
    assertionResponse: unknown,
    deviceLabel?: string,
  ) =>
    api<{ session: { sessionId: string; token: string; expiresIn: number } }>(
      "/staff/passkey/login/verify",
      {
        method: "POST",
        body: JSON.stringify({ challengeId, assertionResponse, deviceLabel }),
      },
    ),
  list: () => api<{ items: StaffPasskeySummary[] }>("/staff/passkey"),
  rename: (id: string, name: string) =>
    api<{ passkey: StaffPasskeySummary }>(`/staff/passkey/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),
  revoke: (id: string) =>
    api<void>(`/staff/passkey/${id}`, { method: "DELETE" }),
};

// =====================================================================
// Staff onboarding wizard (/staff/setup/*). Authenticates with the
// setup-session token in the `X-Staff-Setup-Token` header — NOT a staff
// session. The single-use provisioning token (minted in the main client
// and carried here in the URL) is swapped for that setup token on the
// first `init` call.
// =====================================================================

export type StaffSetupStatus =
  | "pending_profile"
  | "pending_security"
  | "completed";

export interface StaffSetupUser {
  publicId: string;
  displayName: string;
  username: string | null;
}

export interface StaffSetupAccount {
  status: StaffSetupStatus;
  publicKey: string | null;
  completedAt: string | null;
  lastStepData: Record<string, unknown> | null;
}

export interface StaffSetupInitResponse {
  setupSessionToken: string;
  expiresInSeconds: number;
  staffAccount: StaffSetupAccount;
  user: StaffSetupUser | null;
}

/** First wizard call — consumes the single-use provisioning token. */
export function staffSetupInit(
  provisioningToken: string,
): Promise<StaffSetupInitResponse> {
  return api<StaffSetupInitResponse>("/staff/setup/init", {
    method: "POST",
    body: JSON.stringify({ token: provisioningToken }),
  });
}

export interface StaffSetupStateResponse {
  staffAccount: StaffSetupAccount;
  user: StaffSetupUser | null;
}

/** Read-only resume probe using the setup-session token. */
export function staffSetupState(
  setupToken: string,
): Promise<StaffSetupStateResponse> {
  return api<StaffSetupStateResponse>("/staff/setup/state", {
    headers: { "X-Staff-Setup-Token": setupToken },
  });
}

export function staffSetupSaveProfile(
  setupToken: string,
  profile: { displayName?: string; bio?: string; extra?: Record<string, unknown> },
): Promise<{ staffAccount: StaffSetupAccount }> {
  return api<{ staffAccount: StaffSetupAccount }>("/staff/setup/profile", {
    method: "PATCH",
    headers: { "X-Staff-Setup-Token": setupToken },
    body: JSON.stringify(profile),
  });
}

export interface StaffSecurityChallenge {
  nonce: string;
  expiresInSeconds: number;
}

export function staffSetupSecurityChallenge(
  setupToken: string,
  publicKey: string,
): Promise<StaffSecurityChallenge> {
  return api<StaffSecurityChallenge>("/staff/setup/security/challenge", {
    method: "POST",
    headers: { "X-Staff-Setup-Token": setupToken },
    body: JSON.stringify({ publicKey }),
  });
}

export function staffSetupSecurityVerify(
  setupToken: string,
  payload: {
    publicKey: string;
    nonce: string;
    timestamp: number;
    signature: string;
    wordChecks: { index: number; word: string }[];
  },
): Promise<{ staffAccount: StaffSetupAccount }> {
  return api<{ staffAccount: StaffSetupAccount }>("/staff/setup/security/verify", {
    method: "POST",
    headers: { "X-Staff-Setup-Token": setupToken },
    body: JSON.stringify(payload),
  });
}

// =====================================================================
// Staff login (/staff/auth/*). Ed25519 challenge-response against the
// publicKey persisted in `staff_accounts`. The user proves control with
// their staff recovery phrase — derived client-side, never sent. Verify
// mints the `dr_staff_session` cookie on this origin.
// =====================================================================

export interface StaffLoginChallenge {
  nonce: string;
  expiresInSeconds: number;
}

export function staffLoginChallenge(
  publicKey: string,
): Promise<StaffLoginChallenge> {
  return api<StaffLoginChallenge>("/staff/auth/challenge", {
    method: "POST",
    body: JSON.stringify({ publicKey }),
  });
}

export interface StaffLoginResult {
  session: {
    sessionId: string;
    token: string;
    deviceLabel: string | null;
    expiresIn: number;
  };
}

export function staffLoginVerify(payload: {
  publicKey: string;
  nonce: string;
  timestamp: number;
  signature: string;
  deviceLabel?: string;
}): Promise<StaffLoginResult> {
  return api<StaffLoginResult>("/staff/auth/verify", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
